'use client';

import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface TeamMember {
  name: string;
  title: string;
  image: string;
  imageAlt: string;
  bio: string;
  specializations: string[];
  profileSlug: string;
}

interface OurTeamSectionProps {
  title: string;
  subtitle: string;
  description: string;
  teamMembers: TeamMember[];
}

const OurTeamSection: React.FC<OurTeamSectionProps> = ({ title, subtitle, description, teamMembers }) => {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <p className="text-accent font-semibold text-sm uppercase tracking-wider mb-3">
            {subtitle}
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            {title}
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            {description}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {teamMembers.map((member, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="relative h-80 overflow-hidden">
                <AppImage
                  src={member.image}
                  alt={member.imageAlt}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <h3 className="text-2xl font-bold text-white mb-1">
                    {member.name}
                  </h3>
                  <p className="text-accent font-semibold text-sm">
                    {member.title}
                  </p>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-600 mb-6 line-clamp-4">
                  {member.bio}
                </p>

                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-primary mb-3">Practice Areas</h4>
                  <div className="flex flex-wrap gap-2">
                    {member.specializations.slice(0, 3).map((specialization, idx) => (
                      <span 
                        key={idx}
                        className="bg-accent/10 text-primary px-3 py-1 rounded-full text-xs font-medium"
                      >
                        {specialization}
                      </span>
                    ))}
                  </div>
                </div>

                <Link
                  href={`/team-member-profile?member=${member.profileSlug}`}
                  className="block w-full bg-primary text-white text-center py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors duration-300 mb-2"
                >
                  View Full Profile
                </Link>
                
                <Link
                  href="/contact"
                  className="block w-full bg-accent text-primary text-center py-3 rounded-lg font-semibold hover:bg-accent/90 transition-colors duration-300"
                >
                  Schedule Consultation
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurTeamSection;